import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { SpinnerGap, CheckCircle, XCircle, Clock, ArrowClockwise, Queue, VideoCamera, Warning, Pause, Prohibit, Wrench, CloudSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { confirmDialog } from "../lib/uiDialogs";
import BulkReoptimizeProgress from "../components/video/BulkReoptimizeProgress";
import ResumeMenu from "../components/video/ResumeMenu";
import ExitMenu from "../components/video/ExitMenu";
import QueueBriefing from "../components/video/QueueBriefing";
import PipelineWorkerCard from "../components/video/PipelineWorkerCard";
import EtaAttente from "../components/video/EtaAttente";
import MenuContinuerPipeline from "../components/video/MenuContinuerPipeline"; // Tâche 1493
import { diagnostiquerEchec } from "../lib/diagnosticEchec";
import { pipelineStageLabel } from "../lib/videoDetailFormat";

// Tâche 1394 — Ne DÉCIDE rien : désigne seulement les vidéos dont l'échec
// POURRAIT venir d'un fichier absent du stockage. La seule autorité est
// `GET /videos/{id}/storage-status` (voir l'effet de vérification).
export function suspectsMissingFile(v) {
  const errLc = ((v?.error || "") + " " + (v?.pipeline_error || "")).toLowerCase();
  return (
    /object not found/i.test(errLc)
    || /nosuchkey/i.test(errLc)
    || /404/.test(errLc)
    || (v?.pipeline_status === "failed" && v?.pipeline_stage === "audio_extraction")
    || (v?.pipeline_status === "failed" && /extraction audio (echouee|échouée|invalide)/i.test(errLc))
  );
}

export default function VideoQueuePage() {
  const { user } = useAuth();
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  // Eric 2026-07-04 — Actions admin file d'attente ouvertes aux Founder,
  // Admin, Concepteur et Employé avancé (canEditContent) car ils sont TOUS
  // légitimes pour intervenir sur leur pipeline. Le backend scope à l'org
  // active pour les non-founders ; seul le founder voit cross-org.
  const canAdmin = canEditContent(user);
  const isFounder = !!(user?.is_founder || user?.is_super_admin);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState({}); // {videoId: "pause"|"resume"|"cancel"}
  const [globalBusy, setGlobalBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [skipDiarization, setSkipDiarization] = useState(false);
  const [skipVisual, setSkipVisual] = useState(false);
  // Vincent (2026-07-22) — Filtre "afficher uniquement les vidéos avec codec
  // non-standard" pour repérer d'un coup d'œil les vidéos historiques cassées.
  const [showOnlyBadCodec, setShowOnlyBadCodec] = useState(false);
  // Vincent (2026-07-22) — P1-3 : filtre "diarisation suspecte uniquement"
  // (1 seul locuteur détecté sur ≥95% de la durée alors que la vidéo est
  // multi-locuteurs) — même pattern que le filtre codec non-standard.
  const [showOnlySuspect, setShowOnlySuspect] = useState(false);
  // Eric (2026-02) — Compteur incrémenté après POST /bulk-reoptimize pour
  // forcer un refetch immédiat du composant <BulkReoptimizeProgress /> (le
  // polling naturel prendrait sinon jusqu'à 3 s pour découvrir le nouveau job).
  const [bulkJobRefreshTick, setBulkJobRefreshTick] = useState(0);

  const loadFlags = useCallback(async () => {
    if (!canAdmin) return;
    try {
      const { data: res } = await axios.get(`${API}/api/videos/admin/pipeline-flags`, { withCredentials: true });
      setSkipDiarization(!!res.skip_assemblyai_diarization);
      setSkipVisual(!!res.skip_visual_speaker_id);
    } catch (err) {
      // Eric 2026-07-31 (code-review) — Empty catch remplacé par un warn
      // silencieux : loadFlags est non-critique (les toggles se rechargent
      // au prochain refresh) mais on garde une trace en console pour le
      // debug si la fonction admin retourne du 4xx/5xx.
      console.warn("[VideoQueuePage] loadFlags failed:", err?.response?.status, err?.message);
    }
  }, [canAdmin]);

  const loadQueue = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/api/video-queue`, { withCredentials: true });
      setData(res);
    } catch (err) {
      // Eric 2026-07-31 (code-review) — Empty catch remplacé par un warn
      // console (la page continue avec l'état précédent). Si 401 → l'user
      // sera redirigé par l'intercepteur global axios plus haut.
      console.warn("[VideoQueuePage] loadQueue failed:", err?.response?.status, err?.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadQueue(); loadFlags(); const iv = setInterval(loadQueue, 10000); return () => clearInterval(iv); }, [loadQueue, loadFlags]);

  // Tâche 1394 (Eric 2026-06-30) — « Tu m'annonces que le fichier n'est pas
  // arrivé à OVH, puis quand je clique tu me dis qu'il y est. »
  // CAUSE : le bandeau et le bouton rouge partaient d'une SIMPLE HEURISTIQUE
  // sur le texte de l'erreur (« 404 » n'importe où, ou tout échec à l'étape
  // `audio_extraction` — codec refusé, ffmpeg planté, disque plein…). Aucune
  // de ces conditions ne dit que l'objet manque dans OVH. Le serveur, lui,
  // faisait la vraie vérification (`head_object`) au moment du clic et
  // refusait en 400. L'écran accusait donc le stockage à tort, et proposait
  // une suppression définitive sur un diagnostic faux.
  // L'heuristique ne sert plus qu'à décider QUI on va vérifier ; c'est le
  // serveur (`GET /videos/{id}/storage-status`) qui tranche avant tout
  // affichage. Une seule vérification par vidéo — la file se rafraîchit
  // toutes les 10 s, interroger OVH à chaque tour coûterait un appel S3 par
  // vidéo et par tour.
  const [storageChecks, setStorageChecks] = useState({}); // {id: "checking"|"absent"|"present"}
  useEffect(() => {
    const suspects = (data?.queue || []).filter((v) => suspectsMissingFile(v) && !storageChecks[v.id]);
    if (suspects.length === 0) return;
    setStorageChecks((prev) => {
      const next = { ...prev };
      suspects.forEach((v) => { next[v.id] = "checking"; });
      return next;
    });
    suspects.forEach(async (v) => {
      try {
        const { data: res } = await axios.get(`${API}/api/videos/${v.id}/storage-status`, { withCredentials: true });
        setStorageChecks((prev) => ({ ...prev, [v.id]: res?.present ? "present" : "absent" }));
      } catch {
        // On ne peut pas conclure : on n'accuse pas le stockage.
        setStorageChecks((prev) => ({ ...prev, [v.id]: "present" }));
      }
    });
  }, [data, storageChecks]);

  // Vincent B658 (Eric 2026-08-05 SPEC STRICK PROD) — Migration alert/confirm
  // vers toast/confirmDialog. Ce fichier sert de RÉFÉRENCE pour la migration
  // des ~30 autres fichiers taggés (B658).
  const toggleSkipDiarization = async () => {
    try {
      const { data: res } = await axios.post(`${API}/api/videos/admin/toggle-skip-diarization`,
        { value: !skipDiarization }, { withCredentials: true });
      setSkipDiarization(!!res.skip_assemblyai_diarization);
      if (res?.note) toast.warning(res.note, { duration: 10000 });
      toast.success(res.message);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const toggleSkipVisual = async () => {
    try {
      const { data: res } = await axios.post(`${API}/api/videos/admin/toggle-skip-visual-speaker-id`,
        { value: !skipVisual }, { withCredentials: true });
      setSkipVisual(!!res.skip_visual_speaker_id);
      if (res?.note) toast.warning(res.note, { duration: 10000 });
      toast.success(res.message);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const forceUnstickAll = async () => {
    const scopeLabel = isFounder
      ? tr("TOUTES les orgs", "ALL organizations")
      : tr("votre organisation", "your organization");
    const ok = await confirmDialog(
      tr(
        `Réinitialiser toutes les vidéos bloquées de ${scopeLabel} et les remettre en file ? Utilisé quand la file d'attente est morte.`,
        `Reset every stuck video of ${scopeLabel} and put them back in the queue? Used when the queue is dead.`,
      ),
      { danger: true, confirmLabel: tr("Réinitialiser", "Reset") },
    );
    if (!ok) return;
    setGlobalBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/api/videos/admin/force-unstick-all`, {}, { withCredentials: true });
      toast.success(tr(
        `${res.count} vidéo(s) débloquée(s) et remise(s) en file. Environnement : ${window.location.hostname}. Vérifie la file dans 10-30 s.`,
        `${res.count} video(s) unstuck and requeued. Environment: ${window.location.hostname}. Check the queue in 10-30 s.`,
      ));
      await loadQueue();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setGlobalBusy(false);
    }
  };

  const runDiagnostic = async () => {
    setGlobalBusy(true);
    try {
      const { data: res } = await axios.get(`${API}/api/videos/admin/pipeline-diagnostic`, { withCredentials: true });
      setDiag(res);
    } catch (e) {
      toast.error(tr("Erreur diagnostic : ", "Diagnostic error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setGlobalBusy(false);
    }
  };

  const doAction = async (videoId, kind, url) => {
    setActionBusy((b) => ({ ...b, [videoId]: kind }));
    try {
      await axios.post(`${API}${url}`, {}, { withCredentials: true });
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[videoId]; return c; });
    }
  };

  const pause = (id) => doAction(id, "pause", `/api/videos/${id}/queue/pause`);
  const cancel = async (id) => {
    const ok = await confirmDialog(
      tr("Annuler définitivement le traitement de cette vidéo ?", "Permanently cancel the processing of this video?"),
      { danger: true, confirmLabel: tr("Annuler le traitement", "Cancel processing") },
    );
    if (!ok) return;
    doAction(id, "cancel", `/api/videos/${id}/queue/cancel`);
  };
  const restart = (id) => doAction(id, "resume", `/api/videos/${id}/reprocess`);

  // B1122 (Eric 2026-09-08) — Re-transcription depuis la file d'attente (avec
  // ou sans le filtre anti-hallucination) : ces deux reprises n'existaient
  // que dans l'Explorateur, donc invisibles là où on constate justement
  // qu'une transcription a mal tourné. Confirmation obligatoire — la
  // transcription actuelle est effacée et l'appel est facturé.
  const rerunWhisper = async (v, skipDedup) => {
    const ok = await confirmDialog(
      skipDedup
        ? `Re-transcrire « ${v.title} » SANS dédoublonnage ?\n\nLa transcription Whisper actuelle sera effacée et refaite sans le filtre anti-hallucination (tous les segments conservés). L'appel au fournisseur est facturé.`
        : `Recommencer la transcription de « ${v.title} » ?\n\nLa transcription Whisper actuelle sera effacée et refaite depuis le début. L'appel au fournisseur est facturé.`
    );
    if (!ok) return;
    const key = skipDedup ? "rerun-whisper-no-dedup" : "rerun-whisper";
    try {
      setActionBusy((b) => ({ ...b, [v.id]: key }));
      await axios.post(
        `${API}/api/videos/${v.id}/rerun-whisper`,
        skipDedup ? { skip_dedup: true } : {},
        { withCredentials: true, timeout: 60000 }
      );
      toast.success(skipDedup
        ? tr("Transcription relancée sans dédoublonnage", "Transcription restarted without deduplication")
        : tr("Transcription relancée depuis le début", "Transcription restarted from the beginning"));
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Re-transcription impossible", "Could not re-transcribe"));
    } finally {
      setActionBusy((b) => { const nb = { ...b }; delete nb[v.id]; return nb; });
    }
  };
  // Vincent B626 (Eric 2026-07-30 SPEC STRICK) — Bouton « Effacer (vidéo + données) »
  // Vincent B1025 (Eric 2026-02-26 SPEC STRICK PROD) — Renommé de « Retirer »
  // en « Effacer (vidéo + données) » pour distinguer clairement de
  // `detachFromQueue` (retire de la file mais garde les données).
  const removeFromQueue = async (id, title) => {
    const ok = await confirmDialog(
      `Effacer DÉFINITIVEMENT « ${title} » ? Cette action est IRRÉVERSIBLE et supprime la vidéo ET toutes ses données (transcription, Q&R, scripts, procédures, synthèses, tags). Pour simplement la retirer de la file d'attente en conservant ses données, utilisez plutôt « Retirer du pipeline ».`,
      { danger: true, confirmLabel: "Effacer définitivement" },
    );
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [id]: "remove" }));
    try {
      await axios.delete(`${API}/api/videos/${id}`, { withCredentials: true });
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  // Vincent B1025 (Eric 2026-02-26 SPEC STRICK PROD) — « Retirer du pipeline »
  // Retire la vidéo de la File d'attente SANS supprimer ses données.
  // Utile quand une vidéo a été traitée entièrement puis relancée
  // partiellement : l'admin veut sortir la vidéo de la file mais garder
  // toutes les Q&R/scripts/procédures déjà générées. Réversible via
  // `POST /videos/{id}/queue/reattach` (bouton "Remettre dans la file"
  // exposé côté VideosPage).
  const detachFromQueue = async (id, title) => {
    const ok = await confirmDialog(
      `Retirer « ${title} » de la file d'attente ? Le pipeline sera stoppé (si en pause) et la vidéo restera accessible dans /dashboard/videos avec toutes ses données (transcription, Q&R, scripts, procédures). Vous pourrez la remettre dans la file plus tard.`,
      { danger: false, confirmLabel: "Retirer du pipeline" },
    );
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [id]: "detach" }));
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${id}/queue/detach`,
        {},
        { withCredentials: true },
      );
      // Tâche 1487 (Eric) — Dire ce que le retrait a CONCLU : « terminé » dès
      // qu'une connaissance autre que la synthèse existe, « partiellement
      // complété » sinon, avec ce qui manque.
      toast.success(data?.pipeline_complet
        ? tr(
          "Vidéo retirée de la file et pipeline affiché comme TERMINÉ (des connaissances ont déjà été générées). Ses données restent accessibles dans /dashboard/videos.",
          "Video removed from the queue and pipeline shown as COMPLETE (knowledge has already been generated). Its data stays available under /dashboard/videos.",
        )
        : tr(
          `Vidéo retirée de la file. Pipeline affiché comme PARTIELLEMENT COMPLÉTÉ — il manque : ${(data?.manquants || []).join(", ") || "tout"}.`,
          `Video removed from the queue. Pipeline shown as PARTIALLY COMPLETE — missing: ${(data?.manquants || []).join(", ") || "everything"}.`,
        ), { duration: 8000 });
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Retrait de la file impossible", "Could not remove from the queue"));
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  // Vincent B1024 (Eric 2026-02-26 SPEC STRICK PROD) — Réinitialisation
  // d'une vidéo bloquée (is_stuck) directement depuis la File d'attente.
  // Réutilise l'endpoint B1023 (POST /admin/videos/{id}/reset-pipeline)
  // pour purger les 4 flags `*_generation_in_progress` + `generation_status`
  // et remettre `pipeline_status="failed_needs_rerun"` (le vrai bug n'est
  // pas masqué, Vincent voit qu'il faut relancer manuellement).
  const resetStuckPipeline = async (id, title) => {
    const ok = await confirmDialog(
      tr(
        `Réinitialiser le pipeline bloqué de « ${title} » ? Les flags de génération seront effacés pour permettre une nouvelle exécution.`,
        `Reset the stuck pipeline of “${title}”? Generation flags will be cleared so a new run can start.`,
      ),
      { danger: true, confirmLabel: tr("Réinitialiser", "Reset") },
    );
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [id]: "reset-stuck" }));
    try {
      const { data: r } = await axios.post(
        `${API}/api/admin/videos/${id}/reset-pipeline`,
        {},
        { withCredentials: true },
      );
      toast.success(tr(
        `Pipeline réinitialisé (${(r?.flags_reset || []).length} flag(s) purgé(s)).`,
        `Pipeline reset (${(r?.flags_reset || []).length} flag(s) cleared).`,
      ));
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Erreur de réinitialisation", "Reset failed"));
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  // Vincent bogue 669 (Eric 2026-08-05) — Récupération après upload
  // interrompu : le doc vidéo existe mais l'objet OVH n'est PAS arrivé
  // (pod crashé entre insert et put_object_from_file). Le pipeline
  // échoue plus tard sur "Object not found in S3 Canada". Cette action
  // supprime le doc orphelin + navigue vers /upload pour ré-envoyer,
  // en pré-remplissant titre/description/thème récupérés du doc.
  const discardOrphanAndReupload = async (id, title) => {
    const ok = await confirmDialog(
      tr(
        `Le fichier « ${title} » n'est jamais arrivé à OVH (upload interrompu). Supprimer l'entrée orpheline et rouvrir la page d'upload pour renvoyer le fichier ?`,
        `The file “${title}” never reached OVH (interrupted upload). Delete the orphan entry and reopen the upload page to send the file again?`,
      ),
      { danger: true, confirmLabel: tr("Supprimer et ré-uploader", "Delete and re-upload") },
    );
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [id]: "discard-orphan" }));
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${id}/discard-orphan`,
        {},
        { withCredentials: true },
      );
      toast.success(tr(
        "Entrée orpheline supprimée. Sélectionne le fichier pour ré-uploader.",
        "Orphan entry deleted. Pick the file to upload it again.",
      ));
      const prefill = data?.recovery_prefill || {};
      const qs = new URLSearchParams();
      if (prefill.title) qs.set("title", prefill.title);
      if (prefill.description) qs.set("description", prefill.description);
      if (prefill.training_theme) qs.set("theme", prefill.training_theme);
      navigate(`/dashboard/upload${qs.toString() ? "?" + qs.toString() : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Récupération impossible", "Recovery failed"));
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  // Eric 2026-07-20 (iter162) — Ticket #500. Bouton "Debug bundle" par vidéo
  // (fondateur only). Appelle GET /admin/videos/{id}/debug-bundle, copie le
  // JSON dans le presse-papier avec un toast résumé (nb logs, distribution
  // des locuteurs — le smoking gun de #500). Fallback : ouvre le bundle
  // dans un nouvel onglet si clipboard bloqué.
  const copyDebugBundle = async (videoId) => {
    setActionBusy((b) => ({ ...b, [videoId]: "debug" }));
    try {
      const { data: bundle } = await axios.get(
        `${API}/api/admin/videos/${videoId}/debug-bundle`,
        { withCredentials: true }
      );
      const jsonStr = JSON.stringify(bundle, null, 2);
      const nLogs = bundle.pipeline_logs_count || 0;
      const nSeg = bundle.segments_count || 0;
      const spkList = Object.keys(bundle.speaker_distribution || {}).join(", ") || "aucun";
      try {
        await navigator.clipboard.writeText(jsonStr);
        toast.success(tr(
          `Bundle copié dans le presse-papier — ${nLogs} logs · ${nSeg} segments · Locuteurs: ${spkList}`,
          `Bundle copied to the clipboard — ${nLogs} logs · ${nSeg} segments · Speakers: ${spkList}`,
        ));
      } catch (_clipErr) {
        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        toast.info(tr(
          `Presse-papier bloqué — bundle ouvert dans un nouvel onglet. ${nLogs} logs · ${nSeg} segments · Locuteurs: ${spkList}`,
          `Clipboard blocked — bundle opened in a new tab. ${nLogs} logs · ${nSeg} segments · Speakers: ${spkList}`,
        ));
      }
    } catch (e) {
      toast.error(tr("Erreur bundle debug : ", "Debug bundle error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[videoId]; return c; });
    }
  };

  // Vincent B748 (Eric 2026-02-15) — Ré-optimisation par vidéo depuis la file
  // d'attente. Le badge « Codec non-standard » redirigeait vers la page détail
  // (2 clics minimum pour lancer l'action). Nouveau bouton inline pour lancer
  // directement POST /videos/{id}/optimize sans quitter la file. Idéal quand
  // Vincent a 5 vidéos flaggées et veut les relancer en série.
  const handleReoptimize = async (videoId, videoTitle) => {
    const ok = await confirmDialog(
      tr(
        `Ré-optimiser « ${videoTitle} » ?\n\nCette opération FFmpeg peut prendre plusieurs minutes selon la taille du fichier (ré-encodage H.264/yuv420p). Un badge disparaîtra automatiquement en cas de succès.`,
        `Re-optimize “${videoTitle}”?\n\nThis FFmpeg operation can take several minutes depending on the file size (H.264/yuv420p re-encoding). A badge will disappear automatically on success.`,
      ),
      { confirmLabel: tr("Ré-optimiser", "Re-optimize") }
    );
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [videoId]: "reoptimize" }));
    try {
      await axios.post(`${API}/api/videos/${videoId}/optimize`, {}, { withCredentials: true });
      toast.success(tr(
        "Ré-optimisation démarrée. Le badge disparaîtra une fois FFmpeg terminé.",
        "Re-optimization started. The badge will disappear once FFmpeg is done.",
      ));
      // Refresh queue after a short delay to catch the updated flag
      setTimeout(() => { loadQueue(); }, 1500);
    } catch (e) {
      toast.error(tr("Erreur ré-optimisation : ", "Re-optimization error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setActionBusy((b) => { const c = { ...b }; delete c[videoId]; return c; });
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="animate-spin text-blue-500" /></div>;

  const STATUS_CONFIG = {
    // Vincent B660 (Eric 2026-08-05 SPEC STRICK PROD) — Nouveau statut
    // `assembling` : le fichier est en cours d'assemblage + upload OVH en
    // tâche de fond (moved out of /upload/complete request pour éviter
    // Cloudflare 524/520 sur les gros fichiers). Le pipeline ne démarre
    // qu'une fois `pipeline_stage="uploaded"`.
    pending:    { icon: Clock,        color: "text-amber-500",   bg: "bg-amber-50 dark:bg-amber-900/20",   label: tr("En attente", "Pending") },
    processing: { icon: SpinnerGap,   color: "text-blue-500",    bg: "bg-blue-50 dark:bg-blue-900/20",    label: tr("En traitement", "Processing"), spin: true },
    failed:     { icon: XCircle,      color: "text-red-500",     bg: "bg-red-50 dark:bg-red-900/20",      label: tr("Échouée", "Failed") },
    complete:   { icon: CheckCircle,  color: "text-teal-500", bg: "bg-teal-50 dark:bg-teal-900/20", label: tr("Terminé", "Complete") },
    paused:     { icon: Pause,        color: "text-zinc-500",    bg: "bg-zinc-100 dark:bg-zinc-900/40",   label: tr("En pause", "Paused") },
    // Eric 2026-07-07 URGENCE PROD FINALE — Nouveau statut : pipeline mis
    // en pause automatiquement après 10 min bloqué sur la diarisation.
    // L'utilisateur doit choisir : continuer sans diarisation ou annuler.
    paused_awaiting_user: { icon: Warning, color: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-900/20", label: tr("⚠ Pause diarisation — action requise", "⚠ Diarization paused — action required") },
    // Vincent #444 (Eric 2026-07-08 SPEC STRICK) — Pause post-Whisper.
    // Deux variantes selon si un transcript Fireflies a été fourni ou non.
    paused_pending_fusion: { icon: Pause, color: "text-indigo-600", bg: "bg-indigo-50 dark:bg-indigo-900/20", label: tr("Fusion Fireflies prête à réviser — action requise", "Fireflies merge ready for review — action required") },
    paused_pending_extraction: { icon: Pause, color: "text-indigo-600", bg: "bg-indigo-50 dark:bg-indigo-900/20", label: tr("Transcription prête — lancer l'extraction", "Transcript ready — start the extraction") },
    cancelled:  { icon: Prohibit,     color: "text-zinc-400",    bg: "bg-zinc-100 dark:bg-zinc-900/40",   label: tr("Annulée", "Cancelled") },
  };

  const fmtElapsed = (secs) => {
    if (secs == null || secs < 0) return "";
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}min`;
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ${tr("Ko", "KB")}`;
    return `${(bytes / 1024 / 1024).toFixed(1)} ${tr("Mo", "MB")}`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="video-queue-page">
      {/* Vincent B1027 (Eric 2026-02-26 SPEC STRICK PROD) — Bandeau
          pédagogique affiché une fois par device (localStorage) pour
          présenter les 3 nouveaux verbes Reprendre / Retirer / Effacer. */}
      <QueueBriefing />
      {/* Tâche 1423 — Posée EN TÊTE de la file : quand rien n'avance, c'est la
          première question à se poser (l'ouvrier tourne-t-il ?), et la
          réponse doit se lire avant la liste des vidéos. */}
      {canAdmin && <PipelineWorkerCard isFounder={isFounder} />}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">{tr("File d’attente des vidéos", "Video queue")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{tr("Suivi en temps réel du traitement IA de vos vidéos", "Real-time tracking of the AI processing of your videos")}</p>
        </div>
        {/* Eric 2026-07-04 URGENCE PROD — Bouton admin/concepteur/founder pour
            débloquer les vidéos gelées à 5% "Envoi" perpétuel. Founder = toutes
            orgs ; Admin/Concepteur/Employé avancé = leur org active. */}
        {canAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            {isFounder && (
              <>
                <button
                  onClick={toggleSkipDiarization}
                  className={`px-3 py-2 text-xs font-semibold rounded-lg flex items-center gap-1.5 ${skipDiarization ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200"}`}
                  data-testid="queue-toggle-skip-diarization-btn"
                  title={skipDiarization
                    ? tr("AssemblyAI diarization DÉSACTIVÉE (fallback 1 locuteur). Cliquer pour réactiver.", "AssemblyAI diarization DISABLED (single-speaker fallback). Click to re-enable.")
                    : tr("Désactiver AssemblyAI diarization (utile quand la file hang à 55% diarisation)", "Disable AssemblyAI diarization (useful when the queue hangs at 55% diarization)")}
                >
                  {tr("Diarisation : ", "Diarization: ")}{skipDiarization ? "OFF (bypass)" : "ON"}
                </button>
                <button
                  onClick={toggleSkipVisual}
                  className={`px-3 py-2 text-xs font-semibold rounded-lg flex items-center gap-1.5 ${skipVisual ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200"}`}
                  data-testid="queue-toggle-skip-visual-btn"
                  title={skipVisual
                    ? tr("Identification faciale DÉSACTIVÉE. Cliquer pour réactiver.", "Face identification DISABLED. Click to re-enable.")
                    : tr("Désactiver l'identification faciale (utile quand la file hang à 55%)", "Disable face identification (useful when the queue hangs at 55%)")}
                >
                  {tr("Vision : ", "Vision: ")}{skipVisual ? "OFF (bypass)" : "ON"}
                </button>
              </>
            )}
            <button
              onClick={runDiagnostic}
              disabled={globalBusy}
              className="px-3 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs font-semibold rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="queue-diagnostic-btn"
              title={tr("Diagnostic complet du pipeline", "Full pipeline diagnostic")}
            >
              <Warning size={14} /> Diagnostic
            </button>
            <button
              onClick={forceUnstickAll}
              disabled={globalBusy}
              className="px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-500 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="queue-force-unstick-all-btn"
              title={isFounder
                ? tr("Débloque TOUTES les vidéos gelées, toutes orgs (fondateur)", "Unstick EVERY frozen video, all organizations (founder)")
                : tr("Débloque toutes les vidéos gelées de votre organisation", "Unstick every frozen video of your organization")}
            >
              {globalBusy ? <SpinnerGap size={14} className="animate-spin" /> : <Wrench size={14} weight="fill" />}
              {tr("Débloquer TOUT", "Unstick ALL")}{isFounder ? tr(" (toutes orgs)", " (all orgs)") : ""}
            </button>
          </div>
        )}
      </div>

      {/* Eric (2026-02) — Ticket "polling re_optimize_status + progress bar".
          Panneau live du job bulk-reoptimize (auto-hidden si aucun job
          récent). Poll interne toutes les 3 s tant que status="running". */}
      {canAdmin && <BulkReoptimizeProgress refreshTrigger={bulkJobRefreshTick} />}

      {/* Diagnostic panel — visible après avoir cliqué "Diagnostic" */}
      {diag && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 rounded-xl p-4" data-testid="queue-diagnostic-panel">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
              <Warning size={18} weight="fill" /> Diagnostic pipeline — {diag.stuck_count} vidéo(s) bloquée(s)
            </h3>
            <button onClick={() => setDiag(null)} className="text-amber-700 hover:text-amber-900" data-testid="close-diagnostic">✕</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">{tr("Par statut", "By status")}</div>
              <pre className="bg-white/60 dark:bg-black/20 p-2 rounded text-[10px]">{JSON.stringify(diag.by_pipeline_status, null, 2)}</pre>
            </div>
            <div>
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">{tr("Par stage", "By stage")}</div>
              <pre className="bg-white/60 dark:bg-black/20 p-2 rounded text-[10px]">{JSON.stringify(diag.by_pipeline_stage, null, 2)}</pre>
            </div>
            <div>
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">{tr("Nombre de redémarrages en boucle", "Crash-loop count")}</div>
              <pre className="bg-white/60 dark:bg-black/20 p-2 rounded text-[10px]">{JSON.stringify(diag.by_auto_resume_count, null, 2)}</pre>
            </div>
            <div>
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">{tr("Worker en mémoire", "In-memory worker")}</div>
              <pre className="bg-white/60 dark:bg-black/20 p-2 rounded text-[10px]">{JSON.stringify(diag.in_memory_queue, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { id: "enattente",    label: tr("En attente", "Pending"),       value: data?.stats?.pending || 0,    icon: Clock,       color: "text-amber-500" },
          { id: "entraitement", label: tr("En traitement", "Processing"), value: data?.stats?.processing || 0, icon: SpinnerGap,  color: "text-blue-500" },
          { id: "enpause",      label: tr("En pause", "Paused"),          value: data?.stats?.paused || 0,     icon: Pause,       color: "text-zinc-500" },
          { id: "echouees",     label: tr("Échouées", "Failed"),          value: data?.stats?.failed || 0,     icon: XCircle,     color: "text-red-500" },
          { id: "annulees",     label: tr("Annulées", "Cancelled"),       value: data?.stats?.cancelled || 0,  icon: Prohibit,    color: "text-zinc-400" },
          { id: "terminees",    label: tr("Terminées", "Complete"),       value: data?.stats?.complete || 0,   icon: CheckCircle, color: "text-teal-500" },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4" data-testid={`queue-stat-${s.id}`}>
            <s.icon size={20} className={`${s.color} mb-2`} />
            <div className="text-2xl font-bold">{s.value}</div>
            <div className="text-xs text-zinc-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Active Queue */}
      {data?.queue?.length > 0 && (
        <div
          /* B1122 (Eric 2026-09-08) — `overflow-hidden` coupait les menus
             déroulants (« Reprendre », « Sortir de la file ») à la hauteur de
             la ligne : on ne voyait qu'un bandeau blanc vide, d'où « les
             éléments du menu ne sont pas ceux attendus ». */
          className="bg-card border border-border rounded-xl"
          data-testid="active-queue"
        >
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Queue size={18} className="text-blue-500" />
            <h2 className="font-semibold">{tr("File active", "Active queue")} ({data.queue.length})</h2>
          </div>
          <div className="divide-y divide-border">
            {data.queue.map((v) => {
              const cfg = STATUS_CONFIG[v.pipeline_status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              const busy = actionBusy[v.id];
              const canPause = v.pipeline_status === "pending" || v.pipeline_status === "processing";
              // Tâche 1317 (Eric 2026-06-30) — « Reprendre » ne reconnaissait
              // que TROIS états exacts : failed, paused, cancelled. Or le
              // pipeline s'arrête aussi en `paused_diarization`,
              // `paused_extraction`, `paused_fusion`, `paused_pending_fusion`,
              // `paused_pending_extraction` et `paused_awaiting_user`. Dans
              // ces états, « Sortir de la file » restait affiché (lui teste
              // `startsWith("paused")`) mais la relance disparaissait : on
              // pouvait abandonner une vidéo, pas la reprendre. Même règle
              // pour les deux menus désormais.
              // Choix d'Eric : une vidéo encore marquée « en traitement »,
              // même bloquée depuis plus de 30 min, garde uniquement
              // « Réinitialiser » — relancer par-dessus un tour qui tourne
              // encore dupliquerait le travail déjà payé.
              const canRestart = ["failed", "cancelled"].includes(v.pipeline_status)
                || (v.pipeline_status || "").startsWith("paused");
              const canCancel = v.pipeline_status !== "complete" && v.pipeline_status !== "cancelled";
              // Vincent B626 (Eric 2026-07-30 SPEC STRICK) — « Retirer de la liste »
              // visible pour tout pipeline « arrêté » ou « en pause » (paused, paused_*,
              // cancelled, failed). Permet à l'utilisateur de nettoyer la liste sans
              // devoir passer par l'explorateur.
              const canRemove = v.pipeline_status === "cancelled"
                || v.pipeline_status === "failed"
                || (v.pipeline_status || "").startsWith("paused");
              // Tâche 1394 — L'heuristique ne fait plus que SOUPÇONNER ;
              // l'affichage attend le verdict du serveur. Tant qu'il n'est
              // pas rendu, on n'annonce rien : accuser le stockage puis se
              // dédire au clic est pire que d'attendre une seconde.
              const storageVerdict = storageChecks[v.id];
              const isMissingFromStorage = storageVerdict === "absent";
              const isCheckingStorage = storageVerdict === "checking";
              return (
                <div key={v.id} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-2 lg:gap-4" data-testid={`queue-item-${v.id}`}>
                  <div className="flex items-center gap-3 min-w-0 basis-full lg:basis-auto lg:flex-1">
                    <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center relative`}>
                      <Icon size={18} className={`${cfg.color} ${cfg.spin ? "animate-spin" : ""}`} weight={v.pipeline_status === "cancelled" ? "bold" : "regular"} />
                      {v.is_stuck && (
                        <span title={tr("Bloquée depuis plus de 30 min", "Stuck for more than 30 min")} data-testid={`queue-item-stuck-${v.id}`} className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-zinc-900" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      {/* Vincent XLSX 2026-07-09 15h20 #8/#9/#14-#17 (Eric 2026-07-11
                          SPEC STRICK) — Titre cliquable : mène à la page de détail
                          vidéo /dashboard/video/{id}. Auparavant Vincent se plaignait :
                          "les titres des fichiers en liste d'attente ne sont pas cliquables". */}
                      <Link
                        to={`/dashboard/video/${v.id}`}
                        className="text-sm font-medium truncate block hover:text-[#002FA7] hover:underline"
                        data-testid={`queue-item-title-${v.id}`}
                        title={tr("Ouvrir la page de détail de la vidéo", "Open the video detail page")}
                      >
                        {v.title}
                      </Link>
                      {/* Ligne 2 : nom original + taille (cliquable aussi si présent) */}
                      {(v.original_filename || v.file_size) && (
                        <Link
                          to={`/dashboard/video/${v.id}`}
                          className="text-[11px] text-zinc-400 hover:text-[#002FA7] truncate block"
                          data-testid={`queue-item-filename-${v.id}`}
                        >
                          {v.original_filename || ""}
                          {v.file_size ? (v.original_filename ? " · " : "") + fmtSize(v.file_size) : ""}
                        </Link>
                      )}
                      {/* Ligne 3 : détails de progression */}
                      {/* Vincent #419 (Eric 2026-07-06 21h50 SPEC STRICK) — Après
                          annulation, on doit AFFICHER, dans cet ordre exact :
                          (1) pourcentage d'analyse ; (2) nom de la prochaine
                          étape ; (3) numéro de l'étape N/M ; (4) EN ROUGE
                          le temps écoulé depuis le début de la pause. */}
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5 flex-wrap" data-testid={`queue-item-details-${v.id}`}>
                        <span className={`font-semibold ${v.is_stuck ? "text-red-600" : (v.pipeline_status === "cancelled" ? "text-zinc-600" : "")}`}>{cfg.label}</span>
                        {typeof v.progress_pct === "number" && (
                          <span className={v.pipeline_status === "cancelled" ? "text-zinc-600 font-bold" : "text-blue-600 font-bold"} data-testid={`queue-item-progress-${v.id}`}>· {v.progress_pct}%</span>
                        )}
                        {v.stage_label && (
                          <span className="text-zinc-600" data-testid={`queue-item-stage-${v.id}`}>
                            · {v.pipeline_status === "cancelled" ? tr("prochaine étape : ", "next step: ") : ""}
                            {lang === "en" ? (v.stage_label_en || v.stage_label) : v.stage_label}
                          </span>
                        )}
                        {v.step_index && v.step_total && v.queue_state !== "queued" && (
                          <span className="text-zinc-400 font-mono" data-testid={`queue-item-step-${v.id}`}>· {tr("étape", "step")} {v.step_index}/{v.step_total}</span>
                        )}
                        {/* Tâche 1432 — Une vidéo qui attend son tour disait
                            « En traitement · 5 % · Envoi · étape 1/7 », puis
                            « Bloquée » au bout de 30 min. Elle dit maintenant
                            son RANG et depuis quand elle attend : c'est la
                            seule information qui évite une relance inutile,
                            laquelle reprenait la place du travail en cours. */}
                        {v.queue_state === "queued" && v.queue_position && (
                          <span className="text-zinc-600" data-testid={`queue-item-position-${v.id}`}>
                            · {tr(
                              `position ${v.queue_position} sur ${v.queue_waiting_total}`,
                              `position ${v.queue_position} of ${v.queue_waiting_total}`,
                            )}
                          </span>
                        )}
                        {v.queue_state === "queued" && v.waiting_seconds != null && (
                          <span className="text-zinc-400" data-testid={`queue-item-waiting-${v.id}`}>
                            · {tr("en file depuis", "waiting for")} {fmtElapsed(v.waiting_seconds)}
                          </span>
                        )}
                        {/* Tâche 1440 puis 1476 — L'estimation d'attente PAR
                            VIDÉO, en file comme en cours de traitement.
                            Composant partagé avec l'Explorateur et la fiche
                            vidéo (`EtaAttente`) : trois copies finiraient par
                            annoncer trois durées différentes. */}
                        <EtaAttente video={v} />
                        {v.elapsed_seconds != null && v.pipeline_status === "processing" && (
                          <span className="text-zinc-400" data-testid={`queue-item-elapsed-${v.id}`}>· {tr("depuis", "for")} {fmtElapsed(v.elapsed_seconds)}</span>
                        )}
                        {v.pipeline_status === "cancelled" && v.cancelled_at && (
                          <span className="text-red-600 font-semibold" data-testid={`queue-item-cancelled-elapsed-${v.id}`}>
                            · {tr("pause depuis", "paused for")} {fmtElapsed(Math.floor((Date.now() - new Date(v.cancelled_at).getTime()) / 1000))}
                          </span>
                        )}
                        {v.is_stuck && (
                          <span className="text-red-600 font-semibold flex items-center gap-1" data-testid={`queue-item-stuck-badge-${v.id}`}>
                            <Warning size={12} weight="fill" /> {tr("Bloquée", "Stuck")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Tâche 1317 — À 390 px, quatre boutons d'action sur une
                      seule rangée débordaient de l'écran : la relance
                      retrouvée serait restée hors de portée du doigt. La
                      rangée se replie donc sur mobile, et garde son
                      alignement à droite sur grand écran. */}
                  <div className="flex items-center gap-2 flex-wrap justify-end min-w-0 lg:flex-nowrap lg:flex-shrink-0">
                    {/* B1122 — Un message d'échec périmé affiché à côté de
                        « En traitement » faisait croire que le pipeline
                        échouait alors qu'il tournait. On ne montre l'erreur
                        que si le pipeline n'est PAS en cours. */}
                    {/* Tâche 1400 (Eric 2026-09-30) — La file affichait la
                        trace brute tronquée à 200 px : illisible, et sans
                        rien dire de quoi faire. On montre la raison lisible ;
                        la trace complète reste dans l'infobulle, et la fiche
                        vidéo la garde sous « Détails techniques ». */}
                    {v.error && v.pipeline_status !== "processing" && v.pipeline_status !== "pending" && (() => {
                      const d = diagnostiquerEchec(v);
                      const titre = d.reconnu
                        ? (lang === "en" ? d.titre_en : d.titre)
                        : tr(
                            `Échec « ${pipelineStageLabel(d.etape, "fr")} » — cause non reconnue`,
                            `Failed "${pipelineStageLabel(d.etape, "en")}" — cause not recognised`,
                          );
                      const action = lang === "en" ? d.action_en : d.action;
                      return (
                        <span
                          className="text-xs text-red-600 max-w-[240px] truncate hidden lg:inline"
                          title={`${titre}\n\n${tr("À faire : ", "What to do: ")}${action}\n\n${d.technique}`}
                          data-testid={`queue-item-diagnostic-${v.id}`}
                          data-diagnostic-id={d.id}
                        >
                          {titre}
                        </span>
                      );
                    })()}
                    {canPause && (
                      <button
                        onClick={() => pause(v.id)}
                        disabled={!!busy}
                        className="px-2.5 py-1.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 flex items-center gap-1 disabled:opacity-50"
                        data-testid={`queue-pause-${v.id}`}
                        title="Mettre en pause"
                      >
                        <Pause size={14} weight="fill" /> Pause
                      </button>
                    )}
                    {canRestart && (
                      /* Vincent B1026 (Eric 2026-02-26 SPEC STRICK PROD) —
                         Menu déroulant unifié « Reprendre... » qui remplace
                         les 3 boutons ▶ Play jadis affichés côte à côte
                         (Relancer / Continuer sans diarisation / Lancer
                         l'extraction). Adapte ses items au pipeline_status
                         réel de la vidéo, avec une description brève par
                         action pour lever l'ambiguïté visuelle constatée en
                         production (2 boutons ▶ identiques côte à côte). */
                      <ResumeMenu
                        video={v}
                        busy={busy}
                        onRestart={() => restart(v.id)}
                        onResumeNoDiar={async () => {
                          try {
                            setActionBusy((b) => ({ ...b, [v.id]: "resume-no-diar" }));
                            await axios.post(`${API}/api/videos/${v.id}/resume-without-diarization`, {}, { withCredentials: true });
                            await loadQueue();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || tr("Erreur reprise", "Resume failed"));
                          } finally {
                            setActionBusy((b) => { const nb = { ...b }; delete nb[v.id]; return nb; });
                          }
                        }}
                        onRerunWhisper={() => rerunWhisper(v, false)}
                        onRerunWhisperNoDedup={() => rerunWhisper(v, true)}
                      />
                    )}
                    {/* Tâche 1493 (Eric 2026-06) — « Continuer (connaissances) »
                        dans la file aussi : c'est ici qu'on voit une vidéo
                        s'arrêter après la transcription, donc ici qu'on veut
                        choisir par quelle étape reprendre. Remplace l'entrée
                        « Lancer l'extraction » de « Reprendre », qui refaisait
                        la fusion sans le dire. */}
                    {v.pipeline_status !== "processing" && v.pipeline_status !== "pending" && (
                      <MenuContinuerPipeline
                        api={API}
                        video={v}
                        onFait={loadQueue}
                      />
                    )}
                    {/* Vincent bogue 669 (Eric 2026-08-05) — Fichier orphelin
                        (jamais arrivé à OVH). Bouton dédié qui supprime
                        l'entrée + redirige vers /upload avec métadonnées
                        pré-remplies. */}
                    {isCheckingStorage && (
                      <span
                        className="text-xs text-zinc-500 flex items-center gap-1"
                        data-testid={`queue-storage-checking-${v.id}`}
                      >
                        <SpinnerGap size={12} className="animate-spin" />
                        {tr("Vérification du fichier…", "Checking the file…")}
                      </span>
                    )}
                    {isMissingFromStorage && (
                      <button
                        onClick={() => discardOrphanAndReupload(v.id, v.title)}
                        disabled={!!busy}
                        className="px-2.5 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-500 flex items-center gap-1 disabled:opacity-50 font-semibold"
                        data-testid={`queue-discard-orphan-${v.id}`}
                        title={tr("Le fichier n'est pas dans OVH. Supprimer et ré-uploader.", "The file is not in OVH. Delete and re-upload.")}
                      >
                        {busy === "discard-orphan"
                          ? <SpinnerGap size={14} className="animate-spin" />
                          : <CloudSlash size={14} weight="fill" />}
                        {tr("Supprimer et ré-uploader", "Delete and re-upload")}
                      </button>
                    )}
                    {/* Vincent B1024 (Eric 2026-02-26 SPEC STRICK PROD) —
                        Bouton « Réinitialiser » pour les vidéos bloquées
                        (is_stuck). Purge les flags de génération via
                        l'endpoint B1023 pour permettre une relance. */}
                    {canAdmin && v.is_stuck && (
                      <button
                        onClick={() => resetStuckPipeline(v.id, v.title)}
                        disabled={!!busy}
                        className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-500 flex items-center gap-1 disabled:opacity-50 font-semibold"
                        data-testid={`queue-reset-stuck-${v.id}`}
                        title={tr("Réinitialiser le pipeline bloqué (purge les flags de génération)", "Reset the stuck pipeline (clears the generation flags)")}
                        aria-label={tr("Réinitialiser le pipeline bloqué", "Reset the stuck pipeline")}
                      >
                        {busy === "reset-stuck"
                          ? <SpinnerGap size={14} className="animate-spin" />
                          : <ArrowClockwise size={14} weight="bold" />}
                        {tr("Réinitialiser", "Reset")}
                      </button>
                    )}
                    {/* Vincent B1028 (Eric 2026-02-26 SPEC STRICK PROD) —
                        Menu déroulant unifié « Sortir de la file... » qui
                        remplace les 3 boutons jadis affichés côte à côte :
                        Annuler + Retirer du pipeline + Effacer (vidéo +
                        données). Ordre par destructivité croissante, le
                        3e item est marqué `danger` (rouge, séparé). */}
                    {(canCancel || canRemove) && (
                      <ExitMenu
                        video={v}
                        busy={busy}
                        canCancel={canCancel}
                        canRemove={canRemove}
                        onCancel={() => cancel(v.id)}
                        onDetach={() => detachFromQueue(v.id, v.title)}
                        onErase={() => removeFromQueue(v.id, v.title)}
                      />
                    )}
                    {/* Eric 2026-07-20 (iter162) — Debug bundle par vidéo
                        (fondateur only) pour ticket #500 */}
                    {isFounder && (
                      <button
                        onClick={() => copyDebugBundle(v.id)}
                        disabled={busy === "debug"}
                        className="px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1 disabled:opacity-50"
                        data-testid={`queue-debug-bundle-${v.id}`}
                        title={tr("Copier le bundle debug (logs pipeline + distribution locuteurs) pour investigation ticket #500", "Copy the debug bundle (pipeline logs + speaker distribution) for ticket #500")}
                      >
                        {busy === "debug" ? <SpinnerGap size={14} className="animate-spin" /> : "🩺"} Debug
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Completed */}
      {data?.recent_completed?.length > 0 && (
        <div className="bg-card border border-border rounded-xl" data-testid="completed-list">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-teal-500" weight="fill" />
              <h2 className="font-semibold">{tr("Récemment terminées", "Recently completed")}</h2>
              {/* Vincent (2026-07-22) — Compteur "codec non-standard" : combien
                  de vidéos terminées ont un codec problématique (écran noir
                  potentiel dans le browser). */}
              {(() => {
                const badCount = (data?.recent_completed || []).filter((v) => v.codec_non_standard).length;
                return badCount > 0 ? (
                  <>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-[11px] font-semibold rounded-full border border-orange-300"
                      data-testid="completed-codec-nonstandard-count"
                      title={tr(
                        `${badCount} vidéo${badCount > 1 ? "s" : ""} avec codec non-standard détecté${badCount > 1 ? "s" : ""} (optimisation FFmpeg échouée). Peut afficher un écran noir dans Chrome/Firefox.`,
                        `${badCount} video${badCount > 1 ? "s" : ""} with a non-standard codec detected (FFmpeg optimization failed). May show a black screen in Chrome/Firefox.`,
                      )}
                    >
                      &#127916; {badCount} {tr("codec", "codec")}{badCount > 1 ? "s" : ""} {tr("non-standard", "non-standard")}
                    </span>
                    {/* Vincent (2026-07-22) — P1-2 : Bouton "Ré-optimiser
                        tout" en masse. Enfile un job de ré-optim par vidéo
                        `codec_non_standard=true` via POST /videos/bulk-reoptimize. */}
                    {canAdmin && (
                      <button
                        onClick={async () => {
                          if (globalBusy) return;
                          const ok = await confirmDialog(
                            tr(
                              `Ré-optimiser ${badCount} vidéo(s) ? Cette opération peut prendre plusieurs minutes par vidéo (FFmpeg re-encoding H.264/yuv420p).`,
                              `Re-optimize ${badCount} video(s)? This can take several minutes per video (FFmpeg H.264/yuv420p re-encoding).`,
                            ),
                            { confirmLabel: tr("Ré-optimiser", "Re-optimize") },
                          );
                          if (!ok) return;
                          setGlobalBusy(true);
                          try {
                            const { data: res } = await axios.post(`${API}/api/videos/bulk-reoptimize`, {}, { withCredentials: true });
                            toast.success(res?.message || tr(
                              `${res?.queued_count || 0} vidéo(s) enfilée(s)`,
                              `${res?.queued_count || 0} video(s) queued`,
                            ));
                            // Eric (2026-02) — force le composant Progress à
                            // re-fetch immédiatement (au lieu d'attendre 3 s
                            // de polling naturel).
                            setBulkJobRefreshTick((t) => t + 1);
                          } catch (e) {
                            toast.error(tr(
                              `Échec ré-optim en masse : ${e?.response?.data?.detail || e.message}`,
                              `Bulk re-optimization failed: ${e?.response?.data?.detail || e.message}`,
                            ));
                          } finally {
                            setGlobalBusy(false);
                          }
                        }}
                        disabled={globalBusy}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border border-orange-400 bg-white text-orange-700 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="bulk-reoptimize-btn"
                        title={tr(
                          `Enfiler ${badCount} vidéo(s) codec non-standard pour ré-optimisation FFmpeg en arrière-plan.`,
                          `Queue ${badCount} non-standard-codec video(s) for background FFmpeg re-optimization.`,
                        )}
                      >
                        {globalBusy ? <SpinnerGap size={11} className="animate-spin" /> : <Wrench size={11} />}
                        {tr("Ré-optimiser tout", "Re-optimize all")}
                      </button>
                    )}
                  </>
                ) : null;
              })()}
              {/* Vincent (2026-07-22) — P1-3 : Compteur "diarisation suspecte"
                  (1 locuteur unique détecté > 95% alors que multi-locuteurs). */}
              {(() => {
                const suspectCount = (data?.recent_completed || []).filter((v) => v.speaker_diversity_warning).length;
                return suspectCount > 0 ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-[11px] font-semibold rounded-full border border-amber-300"
                    data-testid="completed-suspect-count"
                    title={tr(
                      `${suspectCount} vidéo${suspectCount > 1 ? "s" : ""} avec diarisation suspecte (1 locuteur > 95 % alors que multi-locuteurs).`,
                      `${suspectCount} video${suspectCount > 1 ? "s" : ""} with suspicious diarization (one speaker over 95 % while the video is meant to be multi-speaker).`,
                    )}
                  >
                    &#9888; {tr(
                      `${suspectCount} diarisation${suspectCount > 1 ? "s" : ""} suspecte${suspectCount > 1 ? "s" : ""}`,
                      `${suspectCount} suspicious diarization${suspectCount > 1 ? "s" : ""}`,
                    )}
                  </span>
                ) : null;
              })()}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Filtre : n'afficher que les vidéos avec codec non-standard. Ne
                  s'affiche que s'il y en a au moins une (pas d'UI inutile). */}
              {(data?.recent_completed || []).some((v) => v.codec_non_standard) && (
                <label
                  className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer select-none"
                  data-testid="filter-codec-nonstandard-label"
                >
                  <input
                    type="checkbox"
                    checked={showOnlyBadCodec}
                    onChange={(e) => setShowOnlyBadCodec(e.target.checked)}
                    className="rounded border-zinc-300"
                    data-testid="filter-codec-nonstandard-checkbox"
                  />
                  {tr("Codec non standard uniquement", "Non-standard codec only")}
                </label>
              )}
              {/* Vincent (2026-07-22) — P1-3 : Filtre "diarisation suspecte
                  uniquement" pour repérer d'un coup d'œil les vidéos à
                  ré-analyser (multi-locuteurs manquants). */}
              {(data?.recent_completed || []).some((v) => v.speaker_diversity_warning) && (
                <label
                  className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer select-none"
                  data-testid="filter-suspect-label"
                >
                  <input
                    type="checkbox"
                    checked={showOnlySuspect}
                    onChange={(e) => setShowOnlySuspect(e.target.checked)}
                    className="rounded border-zinc-300"
                    data-testid="filter-suspect-checkbox"
                  />
                  {tr("Diarisation suspecte uniquement", "Suspicious diarization only")}
                </label>
              )}
            </div>
          </div>
          <div className="divide-y divide-border">
            {data.recent_completed
              .filter((v) => (showOnlyBadCodec ? v.codec_non_standard : true))
              .filter((v) => (showOnlySuspect ? v.speaker_diversity_warning : true))
              .map((v) => (
              <div key={v.id} className="px-5 py-3 flex items-center justify-between" data-testid={`completed-${v.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle size={16} weight="fill" className="text-teal-500 flex-shrink-0" />
                  {/* Vincent XLSX 2026-07-09 15h20 #8/#9/#14-#17 — titre cliquable */}
                  <Link
                    to={`/dashboard/video/${v.id}`}
                    className="text-sm font-medium hover:text-[#002FA7] hover:underline truncate"
                    data-testid={`completed-title-${v.id}`}
                    title={tr("Ouvrir la page de détail de la vidéo", "Open the video detail page")}
                  >
                    {v.title}
                  </Link>
                  {/* Eric 2026-07-20 (iter162) — Badge "diarisation suspecte"
                      auto-détecté : la vidéo est censée être multi-locuteurs
                      (>=2 assigned_speaker_ids) mais 1 seul locuteur > 95% du
                      temps → probable diarisation cassée. Ticket #500 prévention. */}
                  {v.speaker_diversity_warning && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-[10px] font-semibold rounded-full border border-amber-300 flex-shrink-0"
                      data-testid={`completed-suspect-${v.id}`}
                      title={tr(
                        `Diarisation suspecte : 1 seul locuteur détecté sur ${v.speaker_diversity_max_pct}% de la durée alors que la vidéo est censée être multi-locuteurs. Clique sur 'Debug' pour investiguer.`,
                        `Suspicious diarization: a single speaker detected over ${v.speaker_diversity_max_pct}% of the duration while the video is supposed to have several. Click “Debug” to investigate.`,
                      )}
                    >
                      <Warning size={11} weight="fill" /> {tr("Diarisation suspecte", "Suspicious diarization")} ({v.speaker_diversity_max_pct}%)
                    </span>
                  )}
                  {/* Vincent (2026-07-22) — Badge "Codec non-standard"
                      auto-détecté : l'optimisation FFmpeg a échoué (pipeline_logs
                      `optimization/failed` sans `success` postérieur) → la vidéo
                      est probablement en HEVC/AV1 → écran noir + audio only
                      dans Chrome/Firefox. Vincent peut ré-optimiser depuis la
                      page détail vidéo (section Debug fondateur). */}
                  {v.codec_non_standard && (
                    <Link
                      to={`/dashboard/video/${v.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-[10px] font-semibold rounded-full border border-orange-300 flex-shrink-0 hover:bg-orange-200"
                      data-testid={`completed-codec-nonstandard-${v.id}`}
                      title={tr(
                        "Codec non-standard détecté : l'optimisation FFmpeg a échoué. La vidéo peut afficher un écran noir dans Chrome/Firefox (HEVC iPhone non ré-encodé). Clique pour ouvrir la page détail et lancer 'Ré-optimiser'.",
                        "Non-standard codec detected: the FFmpeg optimization failed. The video may show a black screen in Chrome/Firefox (iPhone HEVC not re-encoded). Click to open the detail page and run “Re-optimize”.",
                      )}
                    >
                      &#127916; {tr("Codec non-standard", "Non-standard codec")}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Vincent B748 (Eric 2026-02-15) — Bouton inline
                      « Ré-optimiser » quand codec non-standard. Évite le
                      détour par la page détail. Visible aux users canAdmin
                      pour ne pas polluer l'UI Employé. */}
                  {canAdmin && v.codec_non_standard && (
                    <button
                      onClick={() => handleReoptimize(v.id, v.title)}
                      disabled={actionBusy[v.id] === "reoptimize"}
                      className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 text-[11px] rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 flex items-center gap-1 disabled:opacity-50 border border-orange-300"
                      data-testid={`completed-reoptimize-btn-${v.id}`}
                      title={tr(
                        "Relance FFmpeg (ré-encodage H.264/yuv420p) sur cette vidéo. Le badge Codec non-standard disparaîtra en cas de succès.",
                        "Runs FFmpeg again (H.264/yuv420p re-encoding) on this video. The Non-standard codec badge disappears on success.",
                      )}
                    >
                      {actionBusy[v.id] === "reoptimize"
                        ? <SpinnerGap size={12} className="animate-spin" />
                        : <Wrench size={12} />}
                      {tr("Ré-optimiser", "Re-optimize")}
                    </button>
                  )}
                  {/* Eric 2026-07-20 (iter162) — Debug bundle sur les vidéos
                      terminées : indispensable pour comparer une vidéo qui
                      marche vs une cassée (ticket #500) */}
                  {isFounder && (
                    <button
                      onClick={() => copyDebugBundle(v.id)}
                      disabled={actionBusy[v.id] === "debug"}
                      className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[11px] rounded hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1 disabled:opacity-50"
                      data-testid={`completed-debug-bundle-${v.id}`}
                      title={tr("Copier le bundle debug (logs pipeline + distribution locuteurs)", "Copy the debug bundle (pipeline logs + speaker distribution)")}
                    >
                      {actionBusy[v.id] === "debug" ? <SpinnerGap size={12} className="animate-spin" /> : "🩺"} Debug
                    </button>
                  )}
                  <span className="text-xs text-zinc-500">{v.completed_at ? new Date(v.completed_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA") : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!data?.queue?.length && !data?.recent_completed?.length && (
        <div className="text-center py-16">
          <VideoCamera size={48} className="mx-auto mb-4 text-zinc-300" />
          <h3 className="font-medium text-lg">{tr("Aucune vidéo en file d’attente", "No video in the queue")}</h3>
          <p className="text-sm text-zinc-500 mt-1">{tr("Importez une vidéo pour commencer le traitement IA.", "Upload a video to start the AI processing.")}</p>
        </div>
      )}
    </div>
  );
}
